import { Card, Grid, Typography, Button, FormControlLabel, Checkbox, TextField, Box, Alert, AlertTitle, Divider, Tooltip, capitalize } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react'
import { Field, FieldArray, FormikProvider, useFormik } from 'formik';

import * as Yup from "yup";
import api from '../../../redux/features/auth/axiosAuthHelper';
import dayjs from 'dayjs';
import { TextEditField, TextEditReadOnlyField, FormDatePicker, TestTypeSelectField, StripTextEditField, SelectEditField, StripSelectEditField, TwolineStripTextEditField, StripSelectReadOnlyEditField, StripTextReadOnlyEditField, TwolineStripReadOnlyTextEditField } from '../fields/fields';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import colorConfigs from '../../../configs/colorConfigs';
import { AuditSessionStorageKey, AuditTableParameterStorageService } from '../../../services/sessionStorage';
import RareButton from '../buttons/RareButton';
import { Audit } from "../types";
import { drugCodes } from '../CustomHooks';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { v4 as uuidv4 } from 'uuid';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

interface AuditDetailProps {
    audit_id?: any | null;
    handleRefreshButton: () => void;
    handleBackButton: () => void;
}


const AuditDetailCard: React.FC<AuditDetailProps> = ({ audit_id, handleRefreshButton, handleBackButton }) => {
    const authData = useSelector((state: RootState) => state.auth);
    const [errors, setErrors] = useState<string | null>(null);
    const [initialValues, setInitialValues] = useState<any>({})
    const [updatedValues, setUpdatedValues] = useState<any>({})
    const [hasMadeChanges, setHasMadeChanges] = useState<boolean>(false)
    const [audit, setAudit] = useState<Audit>();

    const create = !audit_id;

    // Fetch audit data and point_model JSON data
    useEffect(() => {
        if (audit_id) {
            const fetchAudit = async () => {
                try {
                    const url = `audit-logs/${audit_id}`;

                    const response = await api.get(url, {
                        headers: {
                            'Authorization': `Bearer ${authData.access}`
                        }
                    });
                    setAudit(response.data as Audit);
                } catch (error) {
                    console.error('Error fetching audit', error);
                    setErrors(`No Audit found with ID ${audit_id}`)
                    setTimeout(() => {
                        handleBackButton()
                    }, 4000);
                }
            };
            fetchAudit();
        }
    }, [audit_id]);

    const extractErrorMessages = (data: any): string => {
        if (typeof data === 'string') {
            return data;
        }
        if (Array.isArray(data)) {
            return data.map(item => extractErrorMessages(item)).join(', ');
        }
        if (typeof data === 'object' && data !== null) {
            return Object.keys(data)
                .map(key => `${key}: ${extractErrorMessages(data[key])}`)
                .join('; ');
        }
        return String(data);
    };

    // Define validation schema for audit
    const validationSchema = Yup.object().shape({

    });


    const formik = useFormik({
        enableReinitialize: true,
        validateOnChange: false,
        validateOnBlur: false,
        initialValues: {
            id: audit?.id || "",
            user_id: audit?.user_id || "",
            user_email: audit?.user_email || "",
            action: audit?.action ? capitalize(audit.action) : "",
            timestamp: dayjs(audit?.timestamp).format("YYYY-MM-DD HH:mm:ss") || "",
            table_name: audit?.table_name || "",
            changesList: audit
                ? Object.entries(audit.changes).map(
                    ([key, [oldValue, newValue]]) => ({ key, oldValue, newValue })
                )
                : [],
        },
        validationSchema,
        onSubmit: async (values) => {

        },
    });


    // Effect to set initial form values from audit to check for form changes
    useEffect(() => {
        setTimeout(() => {
            setInitialValues(formik.initialValues);
            setUpdatedValues(formik.initialValues);
        }, 500);
        setHasMadeChanges(false);
    }, [audit]);

    // Update the updatedValues state when formik values change
    useEffect(() => {
        setUpdatedValues(formik.values);
    }, [formik.values]);

    // Function to check if form has changed so the save button becomes active on edit
    const hasFormChanged = (flatModel: Record<string, any>, formValues: Record<string, any>): boolean => {
        for (const key in formValues) {
            if (key === "expiry_date") {
                const formDate = dayjs(formValues[key]).format('YYYY-MM-DD');
                const initialDate = create ? dayjs(flatModel[key]).format('YYYY-MM-DD') : dayjs(formValues[key]).format('YYYY-MM-DD');
                if (formDate !== initialDate) {
                    console.log("Changed Date", formDate, initialDate)
                    return true;
                }
            } else {
                if (formValues[key] != flatModel[key]) {
                    console.log("Changed Data", formValues[key], flatModel[key])
                    return true;
                }
            }
        }
        return false;
    };

    // Use Effect to check if any changes ahve been made to the form on Edit
    useEffect(() => {
        if (!create && audit) {
            if (Object.keys(initialValues).length > 0 && Object.keys(updatedValues).length > 0) {
                const formChanged = hasFormChanged(initialValues, updatedValues);
                setHasMadeChanges(formChanged);
            }
        }
    }, [updatedValues, initialValues, audit]);

    // Use Effect set save button active on load if Create and if changes are made on Edit
    useEffect(() => {
        if (create) {
            setHasMadeChanges(true);
        } else {
            setHasMadeChanges(false);
        }
    }, [create]);

    return (
        <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit} style={{ overflow: 'auto' }}>
                <Box data-testid="Box-t9qu" sx={{ height: "100%", p: 2, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                    {errors && (
                        <Alert data-testid="Alert-376z" severity="error" style={{ width: "59.5%" }}>
                            <AlertTitle data-testid="AlertTitle-ph2y">Error</AlertTitle>
                            {errors}
                        </Alert>
                    )}
                    <div data-testid="div-bofq" style={{ width: "100%", height: "100%", margin: "auto", padding: "1em", display: "flex", alignItems: "center" }}>
                        <Grid container spacing={5} alignItems="top" justifyContent="center" style={{ minWidth: "100%", padding: 5 }}>
                            <Grid item xs={12} md={12} lg={4} style={{ display: "flex", flexDirection: "column", }}>
                                <Card data-testid="Card-zc78" sx={{ pb: 1, boxShadow: 10, height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <Box data-testid="Box-pkn7" sx={{ display: 'flex' }}>
                                        <Box data-testid="Box-kzz4" sx={{ height: '100%', background: colorConfigs.tables.headBg, display: 'flex', alignItems: 'center', borderBottomRightRadius: 3 }}>
                                            <Typography data-testid="Typography-c7wt" sx={{ px: 2, py: 0.5, color: '#fff', fontWeight: 'bold', fontSize: '20px' }}>Audit Log Detail</Typography>
                                        </Box>
                                    </Box>
                                    <Box data-testid="Box-dy3p" sx={{ p: 2, pt: 3, height: '100%', display: 'flex', flexDirection: 'column', gap: 3 }}>
                                        <Field data-testid="Field-geyp" name="id" type="text" className="form-control" label="Audit Log ID" component={TextEditReadOnlyField} />
                                        <Box data-testid="Box-biwd" sx={{ display: 'flex', gap: 3 }}>
                                            <Box data-testid="Box-wuoz">
                                                <Field data-testid="Field-cwhv" name="user_id" type="text" className="form-control" label="User ID" component={TextEditReadOnlyField} />
                                            </Box>
                                            <Field data-testid="Field-j51m" name="user_email" type="text" className="form-control" label="User Email" component={TextEditReadOnlyField} />
                                        </Box>
                                    </Box>
                                </Card>

                            </Grid>

                            <Grid item xs={12} md={12} lg={6} style={{ display: "flex", flexDirection: "column", }}>
                                <Card data-testid="Card-do1h" sx={{ pb: 1, boxShadow: 10, height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <Box data-testid="Box-kho6" sx={{ display: 'flex' }}>
                                        <Box data-testid="Box-hfhf" sx={{ height: '100%', background: colorConfigs.tables.headBg, display: 'flex', alignItems: 'center', borderBottomRightRadius: 3 }}>
                                            <Typography data-testid="Typography-2fu6" sx={{ px: 2, py: 0.5, color: '#fff', fontWeight: 'bold', fontSize: '20px' }}>Changes</Typography>
                                        </Box>
                                    </Box>
                                    <Box data-testid="Box-fjnp" sx={{ p: 2, pt: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
                                        <Field data-testid="Field-8m22" name="table_name" type="text" className="form-control" label="Table Name" component={TextEditReadOnlyField} />
                                        <Field data-testid="Field-kss4" name="action" type="text" className="form-control" label="Action" component={TextEditReadOnlyField} />
                                    </Box>
                                    <Box data-testid="Box-iz6z" sx={{ p: 2, pt: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
                                        <FieldArray name="changesList">
                                            {() =>
                                                formik.values.changesList.map((change, idx) => (
                                                    <Box data-testid="Box-h0uu" key={idx} sx={{ display: "flex", gap: 3 }}>
                                                        <Field data-testid="Field-t806"
                                                            name={`changesList.${idx}.key`}
                                                            label="Field"
                                                            component={TextEditReadOnlyField}
                                                        />
                                                        {formik.values.action === "Update" && (
                                                            <Field data-testid="Field-pkx4"
                                                                name={`changesList.${idx}.oldValue`}
                                                                label="Old Value"
                                                                component={TextEditReadOnlyField}
                                                            />
                                                        )}
                                                        <Field data-testid="Field-n47z"
                                                            name={`changesList.${idx}.newValue`}
                                                            label="New Value"
                                                            component={TextEditReadOnlyField}
                                                        />
                                                    </Box>
                                                ))
                                            }
                                        </FieldArray>
                                    </Box>
                                </Card>

                            </Grid>

                        </Grid>
                    </div>
                    <div data-testid="div-u4mn" className="form-group" style={{ width: "100%", display: 'flex', alignItems: 'center', justifyContent: "flex-start" }}>
                        <RareButton data-testid="RareButton-gfgp"
                            onClick={() => handleBackButton()}
                            variant="outlined"
                            sx={{
                                position: "relative",
                                m: 1,
                            }}
                        >
                            Back
                        </RareButton>
                        {/* <RareButton data-testid="RareButton-zcme"
                            type="submit"
                            variant="success"
                            sx={{
                                position: "relative",
                                m: 1,
                            }}
                        >
                            Save Changes
                        </RareButton> */}
                    </div>
                </Box>
            </form>
        </FormikProvider >
    )
}

export default AuditDetailCard;